import { useEffect, useState } from 'react';
import {
  Alert, Button, Chip, CircularProgress, Link, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/EditOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKeyOutlined';
import CardMembershipIcon from '@mui/icons-material/CardMembershipOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import TableViewOutlined from '@mui/icons-material/TableViewOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Link as RouterLink, useRouteLoaderData } from 'react-router';
import { format, isValid, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/GlassCard';
import { listCompanies, provisionAdmin, downloadBackup, startAllBackupsDownload, downloadCompaniesExport } from '@/features/companies/api';
import { apiErrorMessage } from '@/lib/errors';
import { tenantUrl } from '@/lib/tenantUrl';
import type { Company } from '@/features/companies/types';
import type { UserProfile } from '@/auth/session';
import { EditCompanyDialog } from '@/features/companies/EditCompanyDialog';
import { AdminCredentialsDialog } from '@/features/companies/AdminCredentialsDialog';
import type { AdminCredsView } from '@/features/companies/AdminCredentialsDialog';
import { SubscriptionsDialog } from '@/features/companies/SubscriptionsDialog';
import { CompanyListDialog } from '@/features/companies/CompanyListDialog';
import { TaxrisGatewayDialog } from '@/features/companies/TaxrisGatewayDialog';
import { ModulesDialog } from '@/features/companies/ModulesDialog';
import { LastUsedCell } from '@/features/companies/LastUsedCell';

const PAGE_SIZE = 20;
const fmt = (s: string | null) => (s ? format(parseISO(s), 'dd/MM/yyyy') : '—');

/** A picked date as the API wants it. A half-typed date reaches us as an
 *  Invalid Date; treating that as "no filter" keeps the list from blanking out
 *  while the operator is still typing. */
const param = (d: Date | null) => (d && isValid(d) ? format(d, 'yyyy-MM-dd') : null);

/** What each row icon does. `describeChild` on the Tooltips keeps these as
 *  descriptions, so every button keeps its own accessible name. */
const ACTION = {
  edit: 'ແກ້ໄຂ TIN / ເລກອະນຸມັດ',
  admin: 'ສ້າງ / ຣີເຊັດ ລະຫັດ admin',
  subscriptions: 'ການສະໝັກໃຊ້ (subscription)',
  companyList: 'ບໍລິສັດໃນກຸ່ມ / ຕັ້ງ Main',
  taxris: 'ລະບົບ TaxRIS',
  modules: 'ໂມດູນທີ່ເປີດໃຊ້',
  backup: 'Backup ຖານຂໍ້ມູນ',
} as const;

export default function CompaniesPage({
  basePath = '/api/companies',
  title = 'ບໍລິສັດ',
  showCreateChild = true,
  showCreate = true,
}: {
  basePath?: string;
  title?: string;
  showCreateChild?: boolean;
  showCreate?: boolean;
} = {}) {
  // The export endpoint is admin-only; showing the control to everyone else
  // would just hand them a 403 toast.
  const user = useRouteLoaderData('protected') as UserProfile | undefined;
  const isAdmin = user?.role === 'admin';
  const [page, setPage] = useState(0);        // 0-indexed for TablePagination
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // Inclusive whole-day ranges over dateStart and dateExpire; the server widens
  // each "ຫາ" past the end of that day.
  const [startFrom, setStartFrom] = useState<Date | null>(null);
  const [startTo, setStartTo] = useState<Date | null>(null);
  const [expireFrom, setExpireFrom] = useState<Date | null>(null);
  const [expireTo, setExpireTo] = useState<Date | null>(null);
  const [rows, setRows] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Company | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [creds, setCreds] = useState<AdminCredsView | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [backupAllBusy, setBackupAllBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [subsCompanyId, setSubsCompanyId] = useState<number | null>(null);
  const [listCompanyId, setListCompanyId] = useState<number | null>(null);
  const [taxrisCompany, setTaxrisCompany] = useState<Company | null>(null);
  const [modulesCompany, setModulesCompany] = useState<Company | null>(null);

  // debounce search → reset to page 0
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Serialized here rather than in the effect so they can be its dependencies:
  // two different Date objects for the same day must not refetch.
  const startFromParam = param(startFrom);
  const startToParam = param(startTo);
  const expireFromParam = param(expireFrom);
  const expireToParam = param(expireTo);
  const filters = {
    search: debounced,
    startFrom: startFromParam, startTo: startToParam,
    expireFrom: expireFromParam, expireTo: expireToParam,
  };

  /** Picking a date also returns to page 1 — page 4 of the old result set is
   *  usually past the end of the new one. Set together so the list refetches
   *  once, not once per state change. */
  function pickDate(set: (d: Date | null) => void) {
    return (d: Date | null) => { set(d); setPage(0); };
  }

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await listCompanies(page + 1, PAGE_SIZE, filters, basePath);
        if (active) { setRows(res.data); setTotal(res.total); }
      } catch {
        if (active) setError('ບໍ່ສາມາດໂຫຼດລາຍການບໍລິສັດໄດ້');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
    // `filters` is rebuilt every render, so the effect depends on its parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debounced, startFromParam, startToParam, expireFromParam, expireToParam, reloadKey, basePath]);

  async function onProvision(c: Company) {
    setBusyId(c.id);
    try {
      const res = await toast.promise(
        provisionAdmin(c.id, basePath),
        { loading: 'ກຳລັງສ້າງ/ຣີເຊັດ admin...', success: 'ສຳເລັດ', error: (e) => apiErrorMessage(e, 'ບໍ່ສຳເລັດ') },
      );
      setCreds({ ...res, nameLa: c.nameLa, nameEn: c.nameEn, pathName: c.pathName });
    } catch {
      // toast.promise already displayed the error toast
    } finally {
      setBusyId(null);
    }
  }

  async function onBackup(id: number) {
    setBusyId(id);
    try {
      await toast.promise(
        downloadBackup(id, basePath),
        { loading: 'ກຳລັງສ້າງຟາຍ backup...', success: 'ດາວໂຫຼດສຳເລັດ', error: (e) => apiErrorMessage(e, 'backup ບໍ່ສຳເລັດ') },
      );
    } catch {
      // toast.promise already displayed the error toast
    } finally {
      setBusyId(null);
    }
  }

  function onBackupAll() {
    // Native streaming download (straight to disk) — the browser owns progress
    // from here; we can't observe completion or per-DB failures, which live in
    // _backup_report.txt inside the ZIP. Brief cooldown avoids a double-trigger.
    startAllBackupsDownload();
    toast('ເລີ່ມດາວໂຫຼດ backup ທຸກຖານຂໍ້ມູນ — ອາດໃຊ້ເວລາຫຼາຍນາທີ. ຖ້າມີຖານທີ່ບໍ່ສຳເລັດ ຈະລາຍງານໃນ _backup_report.txt ພາຍໃນ ZIP', {
      icon: '⬇️', duration: 8000,
    });
    setBackupAllBusy(true);
    setTimeout(() => setBackupAllBusy(false), 3000);
  }

  async function onExport() {
    setExportBusy(true);
    try {
      // `filters` holds the debounced search, not what the operator is still
      // typing: the file must match the rows on screen.
      const { truncated } = await toast.promise(
        downloadCompaniesExport(filters, basePath),
        { loading: 'ກຳລັງສ້າງຟາຍ Excel...', success: 'ດາວໂຫຼດສຳເລັດ', error: (e) => apiErrorMessage(e, 'export ບໍ່ສຳເລັດ') },
      );
      if (truncated) {
        toast('ຟາຍນີ້ບໍ່ຄົບ — ຂໍ້ມູນຫຼາຍເກີນຂີດຈຳກັດ ຈຶ່ງຕັດທ້າຍອອກ ແລະ ຖັນ "ຊ້ຳ" ອາດນັບບໍ່ຄົບ', {
          icon: '⚠️', duration: 10000,
        });
      }
    } catch {
      // toast.promise already displayed the error toast
    } finally {
      setExportBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h3">{title}</Typography>
        <Stack direction="row" spacing={1}>
          {isAdmin && (
            <Button variant="outlined" startIcon={<TableViewOutlined />} disabled={exportBusy} onClick={() => void onExport()}>
              Export Excel
            </Button>
          )}
          <Button variant="outlined" startIcon={<DownloadOutlined />} disabled={backupAllBusy} onClick={onBackupAll}>
            Backup ທັງໝົດ
          </Button>
          {showCreate && (
            <Button component={RouterLink} to="/companies/new" variant="contained">
              + ສ້າງບໍລິສັດ
            </Button>
          )}
        </Stack>
      </Stack>
      <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <TextField
            label="ຄົ້ນຫາ (ຊື່ / TIN / pathName)" value={search} fullWidth size="small"
            onChange={(e) => setSearch(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
            {([
              ['ເລີ່ມ ຈາກ', startFrom, setStartFrom],
              ['ເລີ່ມ ຫາ', startTo, setStartTo],
              ['ໝົດອາຍຸ ຈາກ', expireFrom, setExpireFrom],
              ['ໝົດອາຍຸ ຫາ', expireTo, setExpireTo],
            ] as const).map(([label, value, set]) => (
              <DatePicker
                key={label} label={label} value={value} onChange={pickDate(set)} format="dd/MM/yyyy"
                slotProps={{ field: { clearable: true }, textField: { size: 'small' } }}
              />
            ))}
          </Stack>
        </Stack>
        {error && <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ຊື່ (ລາວ)</TableCell>
                <TableCell>ຊື່ (ອັງກິດ)</TableCell>
                <TableCell>pathName</TableCell>
                <TableCell>ເລີ່ມ</TableCell>
                <TableCell>ໝົດອາຍຸ</TableCell>
                <TableCell>TIN</TableCell>
                <TableCell>ປະເພດ</TableCell>
                <TableCell>ເລກອະນຸມັດ</TableCell>
                <TableCell>ວັນທີອະນຸມັດ</TableCell>
                <TableCell>ໃຊ້ງານລ່າສຸດ</TableCell>
                <TableCell align="right">ແກ້ໄຂ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary' }}>ບໍ່ພົບຂໍ້ມູນ</TableCell></TableRow>
              ) : rows.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.nameLa}</TableCell>
                  <TableCell>{c.nameEn}</TableCell>
                  <TableCell>
                    <Link href={tenantUrl(c.pathName)} target="_blank" rel="noopener" underline="hover">
                      {c.pathName}
                    </Link>
                  </TableCell>
                  <TableCell>{fmt(c.dateStart)}</TableCell>
                  <TableCell>{fmt(c.dateExpire)}</TableCell>
                  <TableCell>{c.taxCode ?? '—'}</TableCell>
                  <TableCell>{c.isMain ? <Chip label="Main" color="primary" size="small" /> : c.isChild ? <Chip label="Child" size="small" /> : '—'}</TableCell>
                  <TableCell>{c.approveNo ?? '—'}</TableCell>
                  <TableCell>{fmt(c.approveDate)}</TableCell>
                  <TableCell><LastUsedCell companyId={c.id} basePath={basePath} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title={ACTION.edit} describeChild>
                      <IconButton size="small" onClick={() => setEditing(c)} aria-label={`edit ${c.nameEn}`}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {/* The two that can go busy are wrapped: a disabled MUI button
                        fires no pointer events, so its tooltip would vanish exactly
                        while the operator is waiting and wondering. */}
                    <Tooltip title={ACTION.admin} describeChild>
                      <span>
                        <IconButton size="small" disabled={busyId === c.id} onClick={() => { if (window.confirm('ສ້າງ / ຣີເຊັດ ລະຫັດ admin ຂອງບໍລິສັດນີ້?')) void onProvision(c); }} aria-label={`admin ${c.nameEn}`}><VpnKeyIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={ACTION.subscriptions} describeChild>
                      <IconButton size="small" onClick={() => setSubsCompanyId(c.id)} aria-label={`subscriptions ${c.nameEn}`}><CardMembershipIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={ACTION.companyList} describeChild>
                      <IconButton size="small" onClick={() => setListCompanyId(c.id)} aria-label={`company-list ${c.nameEn}`}><AccountTreeOutlined fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={ACTION.taxris} describeChild>
                      <IconButton size="small" onClick={() => setTaxrisCompany(c)} aria-label={`taxris ${c.nameEn}`}><ReceiptLongOutlined fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={ACTION.modules} describeChild>
                      <IconButton size="small" onClick={() => setModulesCompany(c)} aria-label={`modules ${c.nameEn}`}><TuneOutlined fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={ACTION.backup} describeChild>
                      <span>
                        <IconButton size="small" disabled={busyId === c.id} onClick={() => void onBackup(c.id)} aria-label={`backup ${c.nameEn}`}><DownloadOutlined fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]} onPageChange={(_, p) => setPage(p)}
        />
      </GlassCard>
      <EditCompanyDialog
        company={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
        basePath={basePath}
      />
      <AdminCredentialsDialog creds={creds} open={creds !== null} onClose={() => setCreds(null)} />
      <SubscriptionsDialog companyId={subsCompanyId} open={subsCompanyId !== null} onClose={() => setSubsCompanyId(null)} basePath={basePath} />
      <TaxrisGatewayDialog
        companyId={taxrisCompany?.id ?? null}
        companyName={taxrisCompany?.nameLa}
        open={taxrisCompany !== null}
        onClose={() => setTaxrisCompany(null)}
        basePath={basePath}
      />
      <ModulesDialog
        companyId={modulesCompany?.id ?? null}
        companyName={modulesCompany?.nameLa}
        open={modulesCompany !== null}
        onClose={() => setModulesCompany(null)}
        basePath={basePath}
      />
      <CompanyListDialog companyId={listCompanyId} open={listCompanyId !== null} onClose={() => setListCompanyId(null)} basePath={basePath} showCreateChild={showCreateChild} onChanged={() => setReloadKey((k) => k + 1)} />
    </Stack>
  );
}
